import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, CATEGORY_LABELS, GENDER_LABELS } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import SaveLookButton from "../components/SaveLookButton";
import { ArrowUpRight, Instagram, MapPin, Heart, Calendar, User } from "lucide-react";
import { toast } from "sonner";

export default function StartupDetail() {
    const { id } = useParams();
    const { user } = useAuth();
    const [s, setS] = useState(null);
    const [loading, setLoading] = useState(true);
    const [following, setFollowing] = useState(false);
    const [count, setCount] = useState(0);
    const [drops, setDrops] = useState([]);

    useEffect(() => {
        api.get(`/startups/${id}`).then((r) => {
            setS(r.data);
            setFollowing(!!r.data.is_following);
            setCount(r.data.follower_count || 0);
        }).catch(() => setS(null)).finally(() => setLoading(false));
        api.get("/drops").then((r) => setDrops((r.data || []).filter((d) => d.startup_id === id)));
        // Fire profile view (fire-and-forget)
        api.post("/analytics/track", { event: "profile_view", startup_id: id }).catch(() => {});
    }, [id]);

    const trackVisit = () => {
        api.post("/analytics/track", { event: "visit_click", startup_id: id }).catch(() => {});
    };

    const toggleFollow = async () => {
        if (!user) { toast.error("Log in to follow brands"); return; }
        try {
            const { data } = await api.post(`/startups/${id}/follow`);
            setFollowing(data.following);
            setCount((c) => c + (data.following ? 1 : -1));
            toast.success(data.following ? `Following ${s.brand_name}` : "Unfollowed");
        } catch { toast.error("Try again"); }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center text-white/50 font-mono-tag text-sm">Loading...</div>;
    if (!s) return <div className="min-h-screen flex items-center justify-center font-display text-3xl">Not found</div>;

    const heroImg = s.banner_url || s.preview_images?.[0] || s.gallery_images?.[0] || "";

    return (
        <main className="min-h-screen">
            <section className="relative h-[55vh] md:h-[60vh] overflow-hidden">
                {heroImg ? (
                    <img src={heroImg} alt={s.brand_name}
                        className="absolute inset-0 w-full h-full object-cover zoom-slow" />
                ) : (
                    <div data-testid="hero-empty-state" className="absolute inset-0 bg-brand-ink flex items-center justify-center">
                        <span className="font-serif-editorial italic text-white/10 text-[16rem] md:text-[24rem] leading-none select-none">
                            {s.brand_name?.[0] || "P"}
                        </span>
                    </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 max-w-[1400px] mx-auto px-4 md:px-10 pb-8 md:pb-10">
                    <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-2 md:mb-3">/ brand profile</p>
                    <h1 data-testid="startup-detail-title" className={`text-4xl sm:text-5xl md:text-8xl tracking-tighter leading-[0.9] break-words ${s.categories?.includes("artisanal") ? "font-serif-editorial italic" : "font-display font-extrabold uppercase"}`}>
                        {s.brand_name}
                    </h1>
                    {s.featured && (
                        <span data-testid="detail-featured-badge" className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 bg-brand-marigold text-black rounded-sm font-mono-tag text-xs uppercase font-bold">
                            ★ Editor's Pick
                        </span>
                    )}
                    <p className="text-white/80 mt-3 text-base md:text-xl max-w-2xl">{s.tagline}</p>
                </div>
            </section>

            <section className="max-w-[1400px] mx-auto px-4 md:px-10 py-10 md:py-14 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
                <div className="md:col-span-2">
                    <p className="font-mono-tag text-xs uppercase text-white/50 mb-3">/ about</p>
                    <p className="text-white/80 text-base md:text-lg leading-relaxed">{s.description}</p>

                    {s.craft_story && (
                        <div className="mt-10 border-l-2 border-brand-saffron pl-6">
                            <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-2">/ the craft</p>
                            <p className="text-white/80 text-base italic">"{s.craft_story}"</p>
                        </div>
                    )}

                    <div className="mt-10 flex flex-wrap gap-2">
                        {s.categories?.map((c) => (
                            <span key={c} className="font-mono-tag text-xs uppercase px-3 py-1.5 bg-brand-saffron text-black rounded-sm">
                                {CATEGORY_LABELS[c] || c}
                            </span>
                        ))}
                        {s.gender?.map((g) => (
                            <span key={g} className="font-mono-tag text-xs uppercase px-3 py-1.5 border border-white/20 text-white rounded-sm">
                                {GENDER_LABELS[g] || g}
                            </span>
                        ))}
                    </div>

                    {drops.length > 0 && (
                        <div className="mt-14">
                            <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ campaigns from this brand ({drops.length})</p>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {drops.map((d) => (
                                    <div key={d.id} className="relative aspect-[4/5] rounded-md overflow-hidden border border-white/10 group">
                                        <img src={d.image_url} alt={d.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
                                        <p className="absolute bottom-3 left-3 right-3 font-display font-bold text-white leading-tight">{d.title}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {s.gallery_images?.length > 0 ? (
                        <div className="mt-14">
                            <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ gallery ({s.gallery_images.length})</p>
                            <div data-testid="startup-gallery" className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                {s.gallery_images.map((src, i) => (
                                    <div key={i} className="relative aspect-square rounded-md overflow-hidden border border-white/10 hover:border-brand-saffron transition-colors group">
                                        <a href={src} target="_blank" rel="noopener noreferrer"
                                            data-testid={`gallery-image-${i}`}
                                            className="absolute inset-0">
                                            <img src={src} alt={`${s.brand_name} ${i + 1}`}
                                                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        </a>
                                        <SaveLookButton imageUrl={src} title={`${s.brand_name} look ${i + 1}`} source="brand"
                                            startupId={s.id} className="absolute top-2 right-2 z-10" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="mt-14">
                            <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ gallery</p>
                            <div data-testid="gallery-empty-state" className="rounded-md border border-dashed border-white/15 bg-white/[0.02] py-16 flex flex-col items-center justify-center gap-2">
                                <p className="font-serif-editorial italic text-white/40 text-lg">No gallery photos yet</p>
                                <p className="font-mono-tag text-[10px] uppercase tracking-[0.3em] text-white/30">This brand hasn't uploaded images</p>
                            </div>
                        </div>
                    )}
                </div>
                <aside className="grain-card p-6 rounded-md h-fit md:sticky md:top-24 space-y-4">
                    <p className="font-mono-tag text-xs uppercase text-white/50">/ visit brand</p>
                    <a href={s.website_url} target="_blank" rel="noopener noreferrer"
                       onClick={trackVisit}
                       data-testid="startup-visit-website-btn">
                        <Button className="w-full bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold py-6 text-base rounded-md">
                            Visit website <ArrowUpRight className="ml-2 w-4 h-4" />
                        </Button>
                    </a>
                    <Button
                        data-testid="startup-follow-btn"
                        onClick={toggleFollow}
                        variant="outline"
                        className={`w-full py-6 text-base rounded-md font-semibold border-2 ${
                            following ? "bg-brand-magenta text-white border-brand-magenta hover:bg-brand-magenta/90"
                                      : "bg-transparent text-white border-white/30 hover:border-brand-magenta hover:text-brand-magenta"
                        }`}
                    >
                        <Heart className={`w-4 h-4 mr-2 ${following ? "fill-current" : ""}`} />
                        {following ? "Following" : "Follow"} · {count}
                    </Button>
                    <div className="space-y-3 text-sm pt-2">
                        {s.founder_name && (
                            <p className="flex items-center gap-2 text-white/70"><User className="w-4 h-4 text-brand-saffron" />{s.founder_name}</p>
                        )}
                        {s.founded_year && (
                            <p className="flex items-center gap-2 text-white/70"><Calendar className="w-4 h-4 text-brand-saffron" />Founded {s.founded_year}</p>
                        )}
                        {s.price_range && (
                            <p className="font-mono-tag text-xs uppercase text-white/60">Price: {s.price_range}</p>
                        )}
                        {s.instagram && (
                            <p className="flex items-center gap-2 text-white/70">
                                <Instagram className="w-4 h-4 text-brand-saffron" />{s.instagram}
                            </p>
                        )}
                        {s.city && (
                            <p className="flex items-center gap-2 text-white/70">
                                <MapPin className="w-4 h-4 text-brand-saffron" />{s.city}
                            </p>
                        )}
                    </div>
                </aside>
            </section>
        </main>
    );
}
